import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, apiError } from "../context/AuthContext";
import { http } from "../lib/api";
import { useSeo } from "../lib/seo";

const Input = (p) => <input {...p} className="w-full h-13 py-3.5 rounded-full border-2 border-border px-5 outline-none focus:border-turq text-navy" />;

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export default function Login() {
  const { user, login, register, googleLogin } = useAuth();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/account";
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Sign in", noindex: true });
  if (user) return <Navigate to={user.role === "admin" && next === "/account" ? "/admin" : next} replace />;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") await login(f.email, f.password);
      else if (mode === "register") await register(f.name, f.email, f.password);
      else {
        const r = await http.post("/auth/forgot-password", { email: f.email });
        toast.success(r.data.message);
        setMode("signin");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const title = { signin: "Welcome back", register: "Create your account", forgot: "Reset your password" }[mode];

  return (
    <section className="min-h-[70vh]" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <h1 className="text-4xl font-extrabold text-navy" data-testid="auth-title">{title}</h1>
          {mode !== "forgot" && (
            <>
              <button onClick={() => googleLogin(next)} className="mt-8 w-full h-14 rounded-full border-2 border-border flex items-center justify-center gap-3 font-semibold text-navy hover:border-navy transition-colors" data-testid="google-login-button">
                <GoogleIcon /> Continue with Google
              </button>
              <div className="my-6 flex items-center gap-3 text-xs text-navy/50 uppercase tracking-widest"><span className="flex-1 h-px bg-border" />or<span className="flex-1 h-px bg-border" /></div>
            </>
          )}
          <form onSubmit={submit} className="space-y-4 mt-6" data-testid="auth-form">
            {mode === "register" && <Input placeholder="Your name" required value={f.name} onChange={set("name")} data-testid="auth-name-input" />}
            <Input type="email" placeholder="Email" required value={f.email} onChange={set("email")} data-testid="auth-email-input" />
            {mode !== "forgot" && <Input type="password" placeholder="Password (8+ characters)" required minLength={mode === "register" ? 8 : 1} value={f.password} onChange={set("password")} data-testid="auth-password-input" />}
            <button disabled={busy} className="w-full h-14 rounded-full bg-sun text-navy font-extrabold hover:bg-[#ffe34d] transition-colors disabled:opacity-60" data-testid="auth-submit-button">
              {busy ? "Please wait..." : { signin: "Sign in", register: "Create account", forgot: "Send reset link" }[mode]}
            </button>
          </form>
          <div className="mt-6 flex flex-wrap justify-between gap-2 text-sm">
            {mode === "signin" ? (
              <>
                <button onClick={() => setMode("register")} className="font-semibold text-[#0078a0]" data-testid="switch-to-register">New here? Create an account</button>
                <button onClick={() => setMode("forgot")} className="text-navy/60" data-testid="switch-to-forgot">Forgot password?</button>
              </>
            ) : (
              <button onClick={() => setMode("signin")} className="font-semibold text-[#0078a0]" data-testid="switch-to-signin">Already have an account? Sign in</button>
            )}
          </div>
          <p className="mt-8 text-sm text-navy/60">Just want to check out? <Link to="/checkout" className="underline" onClick={() => navigate("/checkout")}>Continue as guest</Link></p>
        </div>
      </div>
    </section>
  );
}

export const ResetPassword = () => {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/auth/reset-password", { token: sp.get("token"), password: pw });
      toast.success("Password updated — please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <section className="max-w-md mx-auto px-6 py-24" data-testid="reset-password-page">
      <h1 className="text-4xl font-extrabold text-navy">Choose a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Input type="password" required minLength={8} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        <button className="w-full h-14 rounded-full bg-sun font-extrabold text-navy" data-testid="reset-password-submit">Update password</button>
      </form>
    </section>
  );
};
